<template>
  <div class="flex-custom">
    <div class="item-merch__wrapper">
      <h1
        v-if="data.title"
        class="item-merch__wrapper__title"
      >
        {{ data.title }}
      </h1>
      <div class="item-merch__tab-wrapper">
        <template
          v-for="(item, i) in tabs"
          :key="item.merch_id || `tab-${i}`"
        >
          <a
            class="item-merch__tab-wrapper__tab"
            :class="{'active': i === selected}"
            href="javascript:;"
            @click="changeTab(i)"
          >
            {{ item.tab_name }}
          </a>
        </template>
      </div>
    </div>

    <div class="item-merch__content-wrapper">
      <!-- Error State -->
      <div
        v-if="error"
        class="item-merch__error-state"
      >
        <p>{{ error }}</p>
      </div>

      <template v-if="!error">
        <template
          v-for="(item, i) in tabs"
          :key="item.merch_id || `content-${i}`"
        >
          <div
            class="item-merch__tab-content"
            :class="{'active': i === selected, 'data-loaded': item.merchLoaded}"
          >
            <!-- Loading State -->
            <div
              v-if="loading && i === selected"
              class="item-merch__loading-state"
            >
              <div class="item-merch__loading-spinner" />
              <p>Loading products...</p>
            </div>
            <div
              v-else-if="!item.merchLoaded || item.merch"
            >
              <div class="splide">
                <div class="splide__track">
                  <ul
                    class="splide__list"
                    v-html="item.merch"
                  />
                </div>
              </div>
            </div>
            <div
              v-else
              class="item-merch__slider-empty"
            >
              <p>No Products Available...</p>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import Splide from '@splidejs/splide';

const arrowSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M28.061 17.0612L19.061 26.0612C18.7792 26.343 18.397 26.5013 17.9985 26.5013C17.6 26.5013 17.2178 26.343 16.936 26.0612C16.6542 25.7794 16.4959 25.3972 16.4959 24.9987C16.4959 24.6002 16.6542 24.218 16.936 23.9362L23.3748 17.5H4.99976C4.60193 17.5 4.2204 17.3419 3.9391 17.0606C3.65779 16.7793 3.49976 16.3978 3.49976 16C3.49976 15.6022 3.65779 15.2206 3.9391 14.9393C4.2204 14.658 4.60193 14.5 4.99976 14.5H23.3748L16.9385 8.05998C16.6567 7.77818 16.4984 7.39599 16.4984 6.99747C16.4984 6.59896 16.6567 6.21677 16.9385 5.93497C17.2203 5.65318 17.6025 5.49487 18.001 5.49487C18.3995 5.49487 18.7817 5.65318 19.0635 5.93497L28.0635 14.935C28.2034 15.0745 28.3143 15.2403 28.3899 15.4228C28.4655 15.6054 28.5043 15.801 28.5041 15.9986C28.5038 16.1962 28.4646 16.3918 28.3885 16.5741C28.3125 16.7565 28.2012 16.922 28.061 17.0612Z" fill="currentColor"/></svg>';
const arrowSvgPrev = arrowSvg.replace('<svg ', '<svg style="transform:rotate(180deg)" ');

function repositionItemMerchNav(sliderEl) {
  const arrows = sliderEl.querySelector('.splide__arrows');
  const pagination = sliderEl.querySelector('.splide__pagination');
  const prevBtn = sliderEl.querySelector('.splide__arrow--prev');
  const nextBtn = sliderEl.querySelector('.splide__arrow--next');
  if (!arrows) return;
  if (pagination && nextBtn) arrows.insertBefore(pagination, nextBtn);
  if (prevBtn) prevBtn.innerHTML = arrowSvgPrev;
  if (nextBtn) nextBtn.innerHTML = arrowSvg;

  const pageCount = sliderEl.querySelectorAll('.splide__pagination__page').length;
  arrows.classList.toggle('splide__arrows--hidden', pageCount <= 1);
}

export default {
  name: 'ItemMerch',
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selected: 0,
      loading: false,
      error: null,
    };
  },
  computed: {
    tabs() {
      return this.data['Merch Tabs'] || [];
    },
  },
  mounted() {
    if (this.tabs.length > 0) {
      this.queryMerch(0);
    }
  },
  updated() {
    const that = this;

    that.$el.querySelectorAll('.item-merch__tab-content.data-loaded .splide:not(.splide-initialized)').forEach((sliderEl) => {
      const splide = new Splide(sliderEl, {
        type: 'loop',
        perPage: 5,
        perMove: 1,
        gap: 32,
        arrows: true,
        pagination: true,
        autoplay: false,
        breakpoints: {
          1469: {
            perPage: 3,
            gap: 15,
          },
          1200: {
            perPage: 2,
          },
          991: {
            perPage: 3,
          },
          768: {
            perPage: 2,
          },
          550: {
            perPage: 1,
          },
        },
      }).mount();

      repositionItemMerchNav(sliderEl);
      splide.on('resize updated', () => {
        requestAnimationFrame(() => repositionItemMerchNav(sliderEl));
      });

      sliderEl.classList.add('splide-initialized');
    });

    // Remove RRP text from all RRP spans
    that.$el.querySelectorAll('.rrp span').forEach((rrpSpan) => {
      rrpSpan.textContent = rrpSpan.textContent.replace(/RRP\s*/i, '');
    });

    // Brand label: show image alt text as link text, keep the same anchor href
    that.$el.querySelectorAll('.brand-label').forEach((label) => {
      const anchor = label.closest('a') || label.querySelector('a');
      const img = label.querySelector('img');
      if (anchor && img) {
        const href = anchor.getAttribute('href');
        const alt = img.getAttribute('alt') || '';
        img.remove();
        anchor.textContent = alt;
        anchor.setAttribute('href', href);
      }
    });

    window.fetchLiveData();
  },
  methods: {
    changeTab(num) {
      this.selected = num;
      this.queryMerch(num);
    },
    queryMerch(num) {
      const that = this;
      const tabs = this.data['Merch Tabs'] || [];

      if (!tabs[num] || tabs[num].merchLoaded) return;

      this.loading = true;
      this.error = null;

      $.ajax({
        type: 'post',
        url: '/ajax/merchandising',
        headers: {
          'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
        },
        data: {
          groupMerchandising_ids: [tabs[num].merch_id],
        },
        timeout: 30000,
        success(response) {
          that.loading = false;

          // Needed to reset the script
          setTimeout(() => {
            document.querySelectorAll('.item-merch .item-merch__content-wrapper .active script').forEach((script) => {
              const newScript = document.createElement('script');
              newScript.text = script.text;
              document.body.appendChild(newScript);
              document.body.removeChild(newScript);
            });
          });

          // used to add splide__slide to each tileparent
          const merchItems = response.data.itemmerchandisinghtml[tabs[num].merch_id];
          const doc = new DOMParser().parseFromString(merchItems, 'text/html');

          Array.from(doc.body.children).forEach((item) => item.classList.add('splide__slide'));

          Object.assign(tabs[num], {
            merch: doc.body.innerHTML,
            merchLoaded: true,
          });

          that.$forceUpdate();
        },
        error(xhr, status, err) {
          that.loading = false;
          that.error = `Error loading products: ${status} - ${err}`;
        },
      });
    },
  },
};
</script>
